import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Link2, Unlink, Search, CheckCircle2, AlertCircle, ChevronRight } from 'lucide-react';
import legacyIngressService from '../services/legacyIngressService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import SearchSelect from './shared/SearchSelect';
import './OhipHotelLinkWizard.css';

// Admin › Infra › Legacy Ingress › OHIP Reader › Link to hub.
//
// The legacy OHIP connectors (sqs_<id>_ORACLEOHIP) know a legacy account and an OHIP property code — their HOTELID
// setting — and nothing said which hub partner › chain › hotel they are. This links each one (legacy_connector_links,
// V627). Normally to a hotel, whose chain and partner follow; to a chain or partner when the hub has no hotel yet.
//
// THE SUGGESTION COMES FROM HOTELID. First the confirmed OHIP code → hub hotel link (Billing › OHIP Transactions ›
// Link hotels), then a hub hotel with that code. Codes are not unique across the hub's chains (MIR is Malolo in one
// and Meeru in another), so a code-only match is offered, never assumed, and two candidates are a decision.
//
// INCREMENTAL, RUN AS OFTEN AS NEEDED. Saved links are kept; the page opens on the connectors still NOT linked, so a
// new connector turns up there next time. Saving sends only the rows changed on this run, and "accept suggestions"
// never touches a connector that already has a link. A wrong link is put right from the All view.
//
// A full page, not a modal (STYLE.md), with the step in the URL, like the OHIP hotels wizard whose layout it shares.

const STEPS = [
    { key: 'match', label: 'Match connectors' },
    { key: 'review', label: 'Review and save' },
    { key: 'done', label: 'Done' },
];
const STEP_KEYS = STEPS.map(s => s.key);
const BACK_TO = '/admin/legacy-ingress?tab=ohip';

const keyOf = (t) => (t && t.entityType ? `${t.entityType}:${t.entityId}` : '');

function LegacyConnectorLinkWizard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const stepParam = searchParams.get('step');
    const step = STEP_KEYS.includes(stepParam) ? stepParam : 'match';
    const goStep = useCallback((key) => {
        const next = new URLSearchParams(searchParams);
        next.set('step', key);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    // Unsaved changes on this run: connectorId -> { entityType, entityId } | null (null = remove the link).
    const [pending, setPending] = useState(() => new Map());
    const [show, setShow] = useState('unlinked');
    const [search, setSearch] = useState('');
    // The row whose partner › chain › hotel picker is open, and what is chosen in it so far.
    const [picking, setPicking] = useState(null);
    const [pick, setPick] = useState({ partnerId: null, accountId: null, hotelId: null });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await legacyIngressService.ohipLinks();
            setData(res.data);
            setPending(new Map());
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // ── Lookups ─────────────────────────────────────────────────────────────────────────────────────────
    const hotelById = useMemo(() => new Map((data?.hubHotels || []).map(h => [h.hotelId, h])), [data]);
    const accountById = useMemo(() => new Map((data?.accounts || []).map(a => [a.accountId, a])), [data]);
    const partnerById = useMemo(() => new Map((data?.partners || []).map(p => [p.partnerId, p])), [data]);
    const hotelsByCode = useMemo(() => {
        const m = new Map();
        for (const h of data?.hubHotels || []) {
            const c = String(h.hotelCode || '').trim().toUpperCase();
            if (!c) continue;
            if (!m.has(c)) m.set(c, []);
            m.get(c).push(h);
        }
        return m;
    }, [data]);
    const ohipLinkByCode = useMemo(() => new Map((data?.ohipLinks || []).map(l => [String(l.hotelCode).toUpperCase(), l.hotelId])), [data]);
    // The hconnect partner, which most direct legacy customers sit under: where the picker starts.
    const defaultPartnerId = useMemo(() => (data?.partners || []).find(p => /^hconnect$/i.test(p.partnerName || ''))?.partnerId || null, [data]);

    // Partner › Chain › Hotel for a target, from the hub's own lists.
    const pathOf = useCallback((t) => {
        if (!t || !t.entityType) return null;
        let hotel = null, account = null, partner = null;
        if (t.entityType === 'hotel') { hotel = hotelById.get(t.entityId) || null; account = hotel ? accountById.get(hotel.accountId) : null; }
        if (t.entityType === 'account') account = accountById.get(t.entityId) || null;
        if (t.entityType === 'partner') partner = partnerById.get(t.entityId) || null;
        if (!partner && account) partner = partnerById.get(account.partnerId) || null;
        return {
            partner: partner?.partnerName || null,
            chain: account?.accountName || (hotel?.accountName ?? null),
            hotel: hotel ? hotel.hotelName : null,
            hotelCode: hotel?.hotelCode || null,
            level: t.entityType,
        };
    }, [hotelById, accountById, partnerById]);
    const pathLabel = (p) => (p ? [p.partner, p.chain, p.hotel].filter(Boolean).join(' › ') : '');

    const savedOf = (r) => (r.link ? { entityType: r.link.entityType, entityId: r.link.entityId } : null);
    const linkOf = useCallback((r) => (pending.has(r.connectorid) ? pending.get(r.connectorid) : savedOf(r)), [pending]);

    // Suggestions from the connector's HOTELID: the confirmed OHIP code link if there is one (it settles a code that
    // two chains share), otherwise every hub hotel with that code.
    const suggestionsOf = useCallback((r) => {
        const code = String(r.hotel_code || '').trim().toUpperCase();
        if (!code) return [];
        const confirmed = ohipLinkByCode.get(code);
        if (confirmed && hotelById.has(confirmed)) return [{ hotel: hotelById.get(confirmed), why: 'OHIP code link' }];
        return (hotelsByCode.get(code) || []).map(h => ({ hotel: h, why: 'same hotel code' }));
    }, [ohipLinkByCode, hotelById, hotelsByCode]);

    // ── Step 1: match ───────────────────────────────────────────────────────────────────────────────────
    const connectors = useMemo(() => data?.connectors || [], [data]);
    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return connectors.filter(r => {
            const linked = !!linkOf(r);
            if (show === 'unlinked' && linked) return false;
            if (show === 'suggested' && (linked || !suggestionsOf(r).length)) return false;
            if (show === 'changed' && !pending.has(r.connectorid)) return false;
            if (!needle) return true;
            return [r.connectorid, r.connector_name, r.account_name, r.source_lambda, r.hotel_code, pathLabel(pathOf(linkOf(r)))]
                .some(v => String(v ?? '').toLowerCase().includes(needle));
        }).sort((a, b) => String(a.account_name || '').localeCompare(String(b.account_name || '')) || a.connectorid - b.connectorid);
    }, [connectors, show, search, linkOf, suggestionsOf, pending, pathOf]);

    const counts = useMemo(() => {
        const linked = connectors.filter(r => linkOf(r)).length;
        const oneSuggestion = connectors.filter(r => !linkOf(r) && suggestionsOf(r).length === 1).length;
        return { all: connectors.length, linked, unlinked: connectors.length - linked, oneSuggestion };
    }, [connectors, linkOf, suggestionsOf]);

    const setLink = (r, target) => {
        setPending(prev => {
            const next = new Map(prev);
            if (keyOf(target) === keyOf(savedOf(r))) next.delete(r.connectorid); else next.set(r.connectorid, target || null);
            return next;
        });
        setPicking(null);
    };

    // Only connectors with NO link and exactly ONE suggestion — never overwrites, so it is safe on every run.
    const acceptSingleSuggestions = () => {
        setPending(prev => {
            const next = new Map(prev);
            for (const r of connectors) {
                const s = suggestionsOf(r);
                if (!linkOf(r) && s.length === 1) next.set(r.connectorid, { entityType: 'hotel', entityId: s[0].hotel.hotelId });
            }
            return next;
        });
    };

    const openPicker = (r) => {
        const cur = linkOf(r);
        let start = { partnerId: defaultPartnerId, accountId: null, hotelId: null };
        if (cur && cur.entityType === 'hotel') {
            const h = hotelById.get(cur.entityId);
            start = { partnerId: accountById.get(h?.accountId)?.partnerId || defaultPartnerId, accountId: h?.accountId || null, hotelId: cur.entityId };
        } else if (cur && cur.entityType === 'account') {
            start = { partnerId: accountById.get(cur.entityId)?.partnerId || defaultPartnerId, accountId: cur.entityId, hotelId: null };
        } else if (cur && cur.entityType === 'partner') {
            start = { partnerId: cur.entityId, accountId: null, hotelId: null };
        }
        setPick(start);
        setPicking(r.connectorid);
    };

    const partnerItems = useMemo(() => (data?.partners || []).map(p => ({ id: p.partnerId, label: p.partnerName })), [data]);
    const chainItems = useMemo(() => (data?.accounts || [])
        .filter(a => !pick.partnerId || a.partnerId === pick.partnerId)
        .map(a => ({ id: a.accountId, label: a.accountName })), [data, pick.partnerId]);
    const hotelItems = useMemo(() => (data?.hubHotels || [])
        .filter(h => pick.accountId && h.accountId === pick.accountId)
        .map(h => ({ id: h.hotelId, label: h.hotelName, code: h.hotelCode || undefined })), [data, pick.accountId]);

    // The deepest level chosen in the picker is the link.
    const pickTarget = pick.hotelId ? { entityType: 'hotel', entityId: pick.hotelId }
        : pick.accountId ? { entityType: 'account', entityId: pick.accountId }
        : pick.partnerId ? { entityType: 'partner', entityId: pick.partnerId } : null;

    // ── Step 2: review ──────────────────────────────────────────────────────────────────────────────────
    const changes = useMemo(() => connectors.filter(r => pending.has(r.connectorid)).map(r => ({
        row: r, from: savedOf(r), to: pending.get(r.connectorid),
    })), [connectors, pending]);

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const links = changes.map(c => ({
                connectorId: c.row.connectorid,
                entityType: c.to ? c.to.entityType : null,
                entityId: c.to ? c.to.entityId : null,
            }));
            const res = await legacyIngressService.saveOhipLinks(links);
            setResult(res.data);
            await load();
            goStep('done');
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setBusy(false);
        }
    };

    const levelChip = (p) => (p && p.level !== 'hotel'
        ? <span className="olw-chip olw-chip-warn">{p.level === 'account' ? 'chain only' : 'partner only'}</span> : null);

    // ── Render ──────────────────────────────────────────────────────────────────────────────────────────
    const back = () => navigate(BACK_TO);
    const noTable = data && data.linkTable === false;

    return (
        <div className="olw-page">
            <Button variant="secondary" onClick={back}><ArrowLeft className="icon" /> Back</Button>

            <div className="olw-header">
                <h1>Link legacy OHIP connectors to the hub</h1>
                <p className="olw-subtitle">
                    Say which hub partner › chain › hotel each legacy OHIP connector is. Suggestions come from the
                    connector's HOTELID. Links you save are kept, so run this again whenever new connectors appear.
                </p>
                <div className="olw-steps">
                    {STEPS.map((s, i) => (
                        <span key={s.key} className={`olw-step ${s.key === step ? 'olw-step-active' : ''}`}>
                            <span className="olw-step-num">{i + 1}</span> {s.label}
                            {i < STEPS.length - 1 && <ChevronRight size={14} className="olw-step-sep" />}
                        </span>
                    ))}
                </div>
            </div>

            {error && <div className="olw-error"><AlertCircle size={16} /> {error}</div>}
            {noTable && <div className="olw-error"><AlertCircle size={16} /> legacy_connector_links does not exist yet (flyway V627 has not run) — nothing can be saved.</div>}
            {loading && <div className="olw-loading"><Spinner /></div>}

            {!loading && data && step === 'match' && (
                <div className="olw-card">
                    <div className="olw-summary">
                        <span><strong>{counts.all}</strong> connectors</span>
                        <span><strong>{counts.linked}</strong> linked</span>
                        <span><strong>{counts.unlinked}</strong> not linked</span>
                        {pending.size > 0 && <span className="olw-pending">{pending.size} unsaved change(s)</span>}
                    </div>
                    <div className="olw-controls">
                        <label className="olw-grow">
                            <Search size={14} />
                            <input placeholder="Search connector, legacy account, lambda, HOTELID or hub hotel" value={search} onChange={e => setSearch(e.target.value)} />
                        </label>
                        <select value={show} onChange={e => setShow(e.target.value)}>
                            <option value="unlinked">Not linked</option>
                            <option value="suggested">Not linked, with a suggestion</option>
                            <option value="changed">Changed on this run</option>
                            <option value="all">All</option>
                        </select>
                        <Button variant="secondary" onClick={acceptSingleSuggestions} disabled={!counts.oneSuggestion}>
                            <Link2 className="icon" /> Accept {counts.oneSuggestion} single suggestion(s)
                        </Button>
                    </div>

                    <table className="olw-table">
                        <thead>
                            <tr><th>Connector</th><th>Legacy account</th><th>HOTELID</th><th>Hub partner › chain › hotel</th></tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const target = linkOf(r);
                                const p = pathOf(target);
                                const sugg = suggestionsOf(r).filter(s => keyOf(target) !== `hotel:${s.hotel.hotelId}`);
                                return (
                                    <tr key={r.connectorid} className={pending.has(r.connectorid) ? 'olw-row-changed' : ''}>
                                        <td>{r.connectorid} · {r.connector_name || '?'}<div className="olw-muted">{r.source_lambda || ''}</div></td>
                                        <td>{r.accountid ? `${r.accountid} · ${r.account_name || '?'}` : '—'}</td>
                                        <td>{r.hotel_code ? <code>{r.hotel_code}</code> : <span className="olw-muted">not set</span>}</td>
                                        <td>
                                            {p && (
                                                <div className="olw-linked">
                                                    <span>{pathLabel(p)}{levelChip(p)}
                                                        {p.hotelCode && r.hotel_code && p.hotelCode.toUpperCase() !== r.hotel_code.toUpperCase() && <span className="olw-chip">code {p.hotelCode}</span>}
                                                    </span>
                                                    <button type="button" className="olw-link-btn" onClick={() => setLink(r, null)} title="Remove link"><Unlink size={14} /></button>
                                                </div>
                                            )}
                                            {!p && sugg.length > 0 && (
                                                <div className="olw-suggestions">
                                                    <span className="olw-suggest-heading">{sugg.length === 1 ? 'Suggested' : `${sugg.length} hotels have this code — choose one`}</span>
                                                    {sugg.map(s => (
                                                        <button key={s.hotel.hotelId} type="button" className="olw-suggest"
                                                                onClick={() => setLink(r, { entityType: 'hotel', entityId: s.hotel.hotelId })}>
                                                            <Link2 size={13} /> {pathLabel(pathOf({ entityType: 'hotel', entityId: s.hotel.hotelId }))}
                                                            <span className="olw-muted">({s.why})</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {picking === r.connectorid ? (
                                                <div className="olw-picker">
                                                    <SearchSelect items={partnerItems} value={pick.partnerId} placeholder="Partner…"
                                                                  onChange={id => setPick({ partnerId: id, accountId: null, hotelId: null })} />
                                                    <SearchSelect items={chainItems} value={pick.accountId} placeholder="Chain…"
                                                                  disabled={!pick.partnerId} emptyLabel="This partner has no chains"
                                                                  onChange={id => setPick(v => ({ ...v, accountId: id, hotelId: null }))} />
                                                    <SearchSelect items={hotelItems} value={pick.hotelId} placeholder="Hotel… (leave empty to link the chain)"
                                                                  disabled={!pick.accountId} emptyLabel="This chain has no hotels"
                                                                  onChange={id => setPick(v => ({ ...v, hotelId: id }))} />
                                                    <div>
                                                        <Button variant="success" disabled={!pickTarget} onClick={() => setLink(r, pickTarget)}>
                                                            {pick.hotelId ? 'Link to this hotel' : pick.accountId ? 'Link to this chain' : 'Link to this partner'}
                                                        </Button>
                                                        <button type="button" className="olw-link-btn" onClick={() => setPicking(null)}>Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button type="button" className="olw-link-btn" onClick={() => openPicker(r)}>
                                                    {p ? 'Change' : 'Choose…'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!rows.length && <tr><td colSpan={4} className="olw-empty">
                                {show === 'unlinked' && counts.all ? 'Every connector is linked.' : 'Nothing to show for this filter.'}
                            </td></tr>}
                        </tbody>
                    </table>

                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => setPending(new Map())} disabled={!pending.size || busy}>Discard changes</Button>
                        <Button variant="success" onClick={() => goStep('review')} disabled={!pending.size || busy || noTable}>
                            Review {pending.size} change(s)
                        </Button>
                    </div>
                </div>
            )}

            {!loading && data && step === 'review' && (
                <div className="olw-card">
                    <div className="olw-summary">
                        <span><strong>{changes.length}</strong> change(s) to save</span>
                        <span className="olw-muted">Every other link stays as it is.</span>
                    </div>
                    <table className="olw-table">
                        <thead>
                            <tr><th>Connector</th><th>HOTELID</th><th>Linked now</th><th>Will be</th></tr>
                        </thead>
                        <tbody>
                            {changes.map(c => {
                                const from = pathOf(c.from);
                                const to = pathOf(c.to);
                                return (
                                    <tr key={c.row.connectorid} className="olw-row-changed">
                                        <td>{c.row.connectorid} · {c.row.connector_name || '?'}<div className="olw-muted">{c.row.account_name || ''}</div></td>
                                        <td>{c.row.hotel_code ? <code>{c.row.hotel_code}</code> : <span className="olw-muted">not set</span>}</td>
                                        <td>{from ? <>{pathLabel(from)}{levelChip(from)}</> : <span className="olw-muted">not linked</span>}</td>
                                        <td>{to ? <>{pathLabel(to)}{levelChip(to)}</> : <span className="olw-muted">link removed</span>}</td>
                                    </tr>
                                );
                            })}
                            {!changes.length && <tr><td colSpan={4} className="olw-empty">No changes — go back and match some connectors.</td></tr>}
                        </tbody>
                    </table>
                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => goStep('match')} disabled={busy}><ArrowLeft className="icon" /> Match connectors</Button>
                        <Button variant="success" onClick={save} disabled={busy || !changes.length || noTable}>
                            Save {changes.length} change(s)
                        </Button>
                    </div>
                </div>
            )}

            {!loading && data && step === 'done' && (
                <div className="olw-card olw-done">
                    <CheckCircle2 size={28} className="olw-done-icon" />
                    <h2>Links saved</h2>
                    <ul>
                        {result && <li>{result.changed} link(s) changed</li>}
                        <li>{counts.linked} of {counts.all} connectors are linked, {counts.unlinked} not linked</li>
                    </ul>
                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => { setShow('unlinked'); goStep('match'); }}>Link more connectors</Button>
                        <Button variant="success" onClick={back}>Back to OHIP Reader</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LegacyConnectorLinkWizard;
